import { Encoder, ArrayUtils, MGPFallible } from '@everyboard/lib';
import { Coord } from '../../jscaip/Coord';
import { CoordSet } from '../../jscaip/CoordSet';
import { Move } from '../../jscaip/Move';
import { MoveCoord } from '../../jscaip/MoveCoord';
import { MoveCoordToCoord } from '../../jscaip/MoveCoordToCoord';
import { MoveWithTwoCoords } from '../../jscaip/MoveWithTwoCoords';
import { ConspirateursFailure } from './ConspirateursFailure';
export class ConspirateursMoveDrop extends MoveCoord {
    static encoder = MoveCoord.getEncoder(ConspirateursMoveDrop.of);
    static of(coord) {
        return new ConspirateursMoveDrop(coord);
    }
    constructor(coord) {
        super(coord.x, coord.y);
    }
    toString() {
        return `ConspirateursMoveDrop(${this.coord.toString()})`;
    }
    equals(other) {
        if (ConspirateursMove.isDrop(other)) {
            return this.coord.equals(other.coord);
        }
        else {
            return false;
        }
    }
}
export class ConspirateursMoveSimple extends MoveCoordToCoord {
    static encoder = MoveWithTwoCoords.getFallibleEncoder(ConspirateursMoveSimple.from);
    static from(start, end) {
        if (start.isAlignedWith(end) && start.getLinearDistanceToward(end) === 1) {
            return MGPFallible.success(new ConspirateursMoveSimple(start, end));
        }
        else {
            return MGPFallible.failure(ConspirateursFailure.SIMPLE_MOVE_SHOULD_BE_OF_ONE_STEP());
        }
    }
    constructor(start, end) {
        super(start, end);
    }
    toString() {
        return `ConspirateursMoveSimple(${this.getStart().toString()} -> ${this.getEnd().toString()})`;
    }
    equals(other) {
        if (ConspirateursMove.isSimple(other)) {
            return super.equals(other);
        }
        else {
            return false;
        }
    }
}
export class ConspirateursMoveJump extends Move {
    coords;
    static encoder = Encoder.tuple([Encoder.list(Coord.encoder)], (move) => [ArrayUtils.copy(move.coords)], (fields) => ConspirateursMoveJump.from(fields[0]).get());
    static from(coords) {
        if (coords.length < 2) {
            return MGPFallible.failure('ConspirateursMoveJump requires at least one jump, so two coords');
        }
        for (let i = 1; i < coords.length; i++) {
            const jumpDirection = coords[i - 1].getDirectionToward(coords[i]);
            if (jumpDirection.isFailure()) {
                return MGPFallible.failure(ConspirateursFailure.INVALID_JUMP());
            }
            const jumpDistance = coords[i - 1].getLinearDistanceToward(coords[i]);
            if (jumpDistance !== 2) {
                return MGPFallible.failure(ConspirateursFailure.INVALID_JUMP());
            }
        }
        const uniqueCoords = new CoordSet(coords);
        if (uniqueCoords.size() === coords.length) {
            return MGPFallible.success(new ConspirateursMoveJump(coords));
        }
        else {
            return MGPFallible.failure(ConspirateursFailure.SAME_LOCATION_VISITED_IN_JUMP());
        }
    }
    constructor(coords) {
        super();
        this.coords = coords;
    }
    addJump(target) {
        const coords = ArrayUtils.copy(this.coords);
        coords.push(target);
        return ConspirateursMoveJump.from(coords);
    }
    getStartingCoord() {
        return this.coords[0];
    }
    getEndingCoord() {
        return this.coords[this.coords.length - 1];
    }
    getLandingCoords() {
        return this.coords.slice(1);
    }
    getJumpedOverCoords() {
        const jumpedOver = [];
        for (let i = 1; i < this.coords.length; i++) {
            const jumpDirection = this.coords[i - 1].getDirectionToward(this.coords[i]).get();
            jumpedOver.push(this.coords[i - 1].getNext(jumpDirection, 1));
        }
        return jumpedOver;
    }
    toString() {
        const jumps = this.coords
            .map((coord) => coord.toString())
            .reduce((coord1, coord2) => coord1 + ' -> ' + coord2);
        return `ConspirateursMoveJump(${jumps})`;
    }
    equals(other) {
        if (ConspirateursMove.isSimple(other) || ConspirateursMove.isDrop(other)) {
            return false;
        }
        else {
            if (other === this)
                return true;
            // Equality only needs to check the first and last coord
            if (this.getStartingCoord().equals(other.getStartingCoord()) === false)
                return false;
            if (this.getEndingCoord().equals(other.getEndingCoord()) === false)
                return false;
            return true;
        }
    }
}
// eslint-disable-next-line @typescript-eslint/no-redeclare
export var ConspirateursMove;
(function (ConspirateursMove) {
    function isDrop(move) {
        return move instanceof ConspirateursMoveDrop;
    }
    ConspirateursMove.isDrop = isDrop;
    function isSimple(move) {
        return move instanceof ConspirateursMoveSimple;
    }
    ConspirateursMove.isSimple = isSimple;
    function isJump(move) {
        return move instanceof ConspirateursMoveJump;
    }
    ConspirateursMove.isJump = isJump;
    ConspirateursMove.encoder = Encoder.disjunction([ConspirateursMove.isDrop, ConspirateursMove.isSimple, ConspirateursMove.isJump], [ConspirateursMoveDrop.encoder, ConspirateursMoveSimple.encoder, ConspirateursMoveJump.encoder]);
})(ConspirateursMove || (ConspirateursMove = {}));
